import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Divider,
  Checkbox,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, StaticDatePicker } from '@mui/x-date-pickers';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { format, addDays, parseISO, startOfDay, isSameDay } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import { recurringEventApi } from '../api';
import { Court, RecurrenceRule } from '../api_types';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { alpha } from '@mui/material/styles';

interface BusinessHours {
  [key: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
  sunday: { isClosed: boolean; openTime: string; closeTime: string };
  monday: { isClosed: boolean; openTime: string; closeTime: string };
  tuesday: { isClosed: boolean; openTime: string; closeTime: string };
  wednesday: { isClosed: boolean; openTime: string; closeTime: string };
  thursday: { isClosed: boolean; openTime: string; closeTime: string };
  friday: { isClosed: boolean; openTime: string; closeTime: string };
  saturday: { isClosed: boolean; openTime: string; closeTime: string };
}

interface DateBlockerModalProps {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  businessId: string;
  courts: Court[];
  onSave: () => Promise<void>;
  businessHours: BusinessHours;
}

// Federal holidays for the current year
const getFederalHolidays = (year: number): Date[] => {
  // Helper to find nth day of month (e.g., 3rd Monday)
  const findNthDayOfMonth = (month: number, day: number, n: number): Date => {
    let date = new Date(year, month, 1);
    let count = 0;
    
    while (count < n) {
      if (date.getDay() === day) {
        count++;
      }
      if (count < n) {
        date.setDate(date.getDate() + 1);
      }
    }
    
    return date;
  };
  
  // Find last Monday of May (Memorial Day)
  const findLastMonday = (month: number): Date => {
    const lastDay = new Date(year, month + 1, 0);
    const day = lastDay.getDay();
    // Calculate days to subtract to get to the last Monday
    const daysToSubtract = day === 0 ? 6 : day - 1;
    lastDay.setDate(lastDay.getDate() - daysToSubtract);
    return lastDay;
  };
  
  // Calculate Thanksgiving (4th Thursday in November)
  const thanksgiving = findNthDayOfMonth(10, 4, 4); // November (0-indexed), Thursday (4), 4th occurrence
  
  const holidays = [
    new Date(year, 0, 1), // New Year's Day
    findNthDayOfMonth(0, 1, 3), // Martin Luther King Jr. Day (3rd Monday in January)
    findNthDayOfMonth(1, 1, 3), // Presidents' Day (3rd Monday in February)
    findLastMonday(4), // Memorial Day (Last Monday in May)
    new Date(year, 6, 4), // Independence Day
    findNthDayOfMonth(8, 1, 1), // Labor Day (1st Monday in September)
    findNthDayOfMonth(9, 1, 2), // Columbus Day (2nd Monday in October)
    new Date(year, 10, 11), // Veterans Day
    thanksgiving, // Thanksgiving Day (4th Thursday in November)
    new Date(year, 11, 25), // Christmas Day
  ];
  
  return holidays;
};

const DateBlockerModal: React.FC<DateBlockerModalProps> = ({
  open,
  onClose,
  facilityId,
  businessId,
  courts,
  onSave,
  businessHours,
}) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [blockFederalHolidays, setBlockFederalHolidays] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useRecurring, setUseRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('weekly');
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [endDate, setEndDate] = useState<Date | null>(addDays(new Date(), 365));
  const [occurrences, setOccurrences] = useState<number | null>(12);
  const [endType, setEndType] = useState<'date' | 'occurrences'>('date');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>([]);
  const currentYear = new Date().getFullYear();
  const [federalHolidays] = useState<Date[]>(getFederalHolidays(currentYear));
  const [datePickerValue, setDatePickerValue] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [useCustomTimeRange, setUseCustomTimeRange] = useState(false);
  const [startTime, setStartTime] = useState<string>('00:00');
  const [endTime, setEndTime] = useState<string>('23:59');
  const [existingBlockedDates, setExistingBlockedDates] = useState<Date[]>([]);
  const [fetchingBlockedDates, setFetchingBlockedDates] = useState(false);

  // Fetch existing blocked dates when the modal opens
  useEffect(() => {
    if (open) {
      fetchExistingBlockedDates();
    }
  }, [open, facilityId]);

  // Fetch existing blocked dates from Firebase
  const fetchExistingBlockedDates = async () => {
    try {
      setFetchingBlockedDates(true);
      const blockedDatesSet = new Set<number>();
      
      // Get blocked events from the events collection
      const eventsRef = collection(db, 'facilities', facilityId, 'events');
      const blockedEventsQuery = query(eventsRef, where("isBlocked", "==", true));
      const blockedEventsSnapshot = await getDocs(blockedEventsQuery);
      
      blockedEventsSnapshot.docs.forEach(doc => {
        const eventData = doc.data();
        if (eventData.date) {
          const eventDate = parseISO(eventData.date);
          blockedDatesSet.add(startOfDay(eventDate).getTime());
        }
      });
      
      // Get recurring blocks from recurring_events collection
      const recurringEventsRef = collection(db, 'facilities', facilityId, 'recurring-events');
      const recurringEventsSnapshot = await getDocs(recurringEventsRef);
      
      // Convert to array of Date objects
      const blockedDates = Array.from(blockedDatesSet).map(timestamp => new Date(timestamp));
      setExistingBlockedDates(blockedDates);
    } catch (error) {
      console.error("Error fetching blocked dates:", error);
    } finally {
      setFetchingBlockedDates(false);
    }
  };

  // Get default open/close times for a given date
  const getBusinessHoursForDate = (date: Date) => {
    const dayOfWeek = date.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    return businessHours[dayName];
  };

  // Set default times based on business hours
  useEffect(() => {
    if (!useCustomTimeRange && selectedDates.length > 0) {
      // Take the first selected date as reference
      const hours = getBusinessHoursForDate(selectedDates[0]);
      if (hours && !hours.isClosed) {
        setStartTime(hours.openTime);
        setEndTime(hours.closeTime);
      } else {
        // Default times if closed or no business hours
        setStartTime('00:00');
        setEndTime('23:59');
      }
    }
  }, [useCustomTimeRange, selectedDates, businessHours]);

  // Add federal holidays to selected dates when the switch is toggled
  useEffect(() => {
    if (blockFederalHolidays) {
      // Add all federal holidays that aren't already selected
      const holidaysToAdd = federalHolidays.filter(holiday => 
        !selectedDates.some(date => 
          startOfDay(date).getTime() === startOfDay(holiday).getTime()
        )
      );
      setSelectedDates([...selectedDates, ...holidaysToAdd]);
    } else {
      // Remove federal holidays from selection
      const nonHolidayDates = selectedDates.filter(date => 
        !federalHolidays.some(holiday => 
          startOfDay(date).getTime() === startOfDay(holiday).getTime()
        )
      );
      setSelectedDates(nonHolidayDates);
    }
  }, [blockFederalHolidays]);

  const handleDateSelect = (date: Date | null) => {
    if (!date) return;
    
    // Always update the visible date in the picker
    setDatePickerValue(date);
    
    // Skip date selection logic when this is called from the DatePicker
    // This prevents double selection since handleDayClick will handle it
  };

  const handleDayClick = (day: Date) => {
    // This is the single place where we'll handle date selection
    const formattedDate = startOfDay(day);
    
    // Check if the date is already blocked in the system
    const isAlreadyBlocked = existingBlockedDates.some(
      blockedDate => startOfDay(blockedDate).getTime() === formattedDate.getTime()
    );
    
    if (isAlreadyBlocked) {
      setError(`${format(formattedDate, 'MMM dd, yyyy')} is already blocked.`);
      return;
    }
    
    // Check if date is already selected
    const dateExists = selectedDates.some(
      d => startOfDay(d).getTime() === formattedDate.getTime()
    );
    
    if (dateExists) {
      // Remove the date if it's already selected
      setSelectedDates(prevDates => 
        prevDates.filter(d => startOfDay(d).getTime() !== formattedDate.getTime())
      );
      setError(null);
    } else {
      // Add the date if it's not already selected
      setSelectedDates(prevDates => [...prevDates, formattedDate]);
      setError(null);
    }
  };

  const handleDayOfWeekToggle = (day: number) => {
    if (selectedDaysOfWeek.includes(day)) {
      setSelectedDaysOfWeek(selectedDaysOfWeek.filter(d => d !== day));
    } else {
      setSelectedDaysOfWeek([...selectedDaysOfWeek, day]);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (selectedDates.length === 0) {
        setError("Please select at least one date to block");
        return;
      }

      if (useRecurring) {
        // For recurring blocks, we need to set up the recurrence rule
        const recurrenceRule: RecurrenceRule = {
          frequency: recurringFrequency,
          interval: recurringInterval,
        };

        if (recurringFrequency === 'weekly' && selectedDaysOfWeek.length > 0) {
          recurrenceRule.daysOfWeek = selectedDaysOfWeek;
        }

        if (endType === 'date' && endDate) {
          recurrenceRule.endDate = format(endDate, 'yyyy-MM-dd');
        } else if (endType === 'occurrences' && occurrences) {
          recurrenceRule.occurrences = occurrences;
        }

        // Create a recurring event for each selected date as the start date
        for (const startDate of selectedDates) {
          const recurringEvent = {
            title: 'Blocked',
            description: 'This time is blocked by the facility',
            startDate: format(startDate, 'yyyy-MM-dd'),
            startTime: startTime,
            endTime: endTime,
            courtIds: courts.map(c => c.id),
            registrationRequired: false,
            status: 'active' as 'active' | 'cancelled' | 'completed',
            recurrenceRule,
          };

          await recurringEventApi.createRecurringEvent(businessId, facilityId, recurringEvent);
        }
      } else {
        // Create individual one-time events for each date using firebase directly
        for (const date of selectedDates) {
          const blockEvent = {
            title: 'Blocked',
            description: 'This time is blocked by the facility',
            date: format(date, 'yyyy-MM-dd'),
            startTime: startTime,
            endTime: endTime,
            courtIds: courts.map(c => c.id),
            registrationRequired: false,
            status: 'scheduled',
            eventTitle: 'Blocked',
            isEvent: true,
            isBlocked: true,
          };
          
          await addDoc(collection(db, 'businesses', businessId, 'facilities', facilityId, 'events'), blockEvent);
        }
      }

      await onSave();
      onClose();
    } catch (error) {
      console.error('Error blocking dates:', error);
      setError('Failed to block dates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Custom day component to highlight selected dates and already blocked dates
  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...other } = props;
    
    const isSelected = selectedDates.some(
      selectedDate => isSameDay(selectedDate, day)
    );
    
    const isFederalHoliday = federalHolidays.some(
      holiday => isSameDay(holiday, day)
    );
    
    const isBlockedDate = existingBlockedDates.some(
      blockedDate => isSameDay(blockedDate, day)
    );
    
    return (
      <PickersDay 
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={isSelected}
        onClick={() => handleDayClick(day)}
        sx={{
          ...(isFederalHoliday && !isSelected && {
            border: '1px solid red',
          }),
          ...(isBlockedDate && {
            backgroundColor: alpha('#e25822', 0.2),
            borderRadius: '50%',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: alpha('#e25822', 0.3),
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '80%',
              height: '80%',
              borderRadius: '50%',
              border: '2px solid #e25822',
            }
          }),
          cursor: isBlockedDate ? 'not-allowed' : 'pointer',
        }}
      />
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Block Dates</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {error && (
          <Box mb={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Select dates to block
            </Typography>
            {fetchingBlockedDates ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading calendar...</Typography>
              </Box>
            ) : (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={datePickerValue}
                  onChange={handleDateSelect}
                  slots={{
                    day: CustomDay
                  }}
                  slotProps={{
                    day: {
                      selectedDates,
                      federalHolidays,
                      existingBlockedDates
                    } as any
                  }}
                />
              </LocalizationProvider>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Options
              </Typography>
              
              {existingBlockedDates.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                    Already Blocked Dates ({existingBlockedDates.length})
                  </Typography>
                  <Box sx={{ maxHeight: 100, overflowY: 'auto' }}>
                    {existingBlockedDates.slice(0, 5).map((date, index) => (
                      <Chip
                        key={index}
                        label={format(date, 'MMM dd, yyyy')}
                        sx={{ m: 0.5, bgcolor: alpha('#e25822', 0.1), color: '#e25822' }}
                        size="small"
                      />
                    ))}
                    {existingBlockedDates.length > 5 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        +{existingBlockedDates.length - 5} more blocked dates
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}

              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={blockFederalHolidays ?? false}
                      onChange={(e) => setBlockFederalHolidays(e.target.checked)}
                    />
                  }
                  label="Block all federal holidays"
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Time Range
              </Typography>
              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomTimeRange ?? false}
                      onChange={(e) => setUseCustomTimeRange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Use custom time range"
                />
              </Box>
              
              <Box mb={2} display="flex" gap={1}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={!useCustomTimeRange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ width: '50%' }}
                />
                <TextField
                  label="End Time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={!useCustomTimeRange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ width: '50%' }}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" mb={2}>
                {!useCustomTimeRange 
                  ? "Using business hours for each day" 
                  : "Custom time range will apply to all selected dates"}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Selected Dates ({selectedDates.length})
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                {selectedDates.map((date, index) => (
                  <Chip
                    key={index}
                    label={format(date, 'MMM dd, yyyy')}
                    onDelete={() => {
                      setSelectedDates(prev => prev.filter((_, i) => i !== index));
                    }}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useRecurring ?? false}
                      onChange={(e) => setUseRecurring(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Create recurring block"
                />
              </Box>
              {useRecurring && (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={recurringFrequency}
                      onChange={(e) => {
                        const freq = e.target.value as 'daily' | 'weekly' | 'monthly' | 'custom';
                        setRecurringFrequency(freq);
                        // Reset days of week when changing frequency
                        if (freq !== 'weekly') {
                          setSelectedDaysOfWeek([]);
                        } else if (selectedDates.length > 0) {
                          // Set the day of week to match the first selected date
                          const firstDayOfWeek = selectedDates[0].getDay();
                          setSelectedDaysOfWeek([firstDayOfWeek]);
                        }
                      }}
                      label="Frequency"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="custom">Annual</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {recurringFrequency === 'weekly' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Repeat on days
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {dayNames.map((day, index) => (
                          <Chip
                            key={day}
                            label={day.substr(0, 3)}
                            onClick={() => handleDayOfWeekToggle(index)}
                            color={selectedDaysOfWeek.includes(index) ? "primary" : "default"}
                            variant={selectedDaysOfWeek.includes(index) ? "filled" : "outlined"}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {recurringFrequency === 'custom' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Annual recurrence
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This will repeat on the same date(s) each year
                      </Typography>
                    </Box>
                  )}
                  
                  <TextField
                    label="Repeat every"
                    type="number"
                    value={recurringInterval}
                    onChange={(e) => setRecurringInterval(Math.max(1, parseInt(e.target.value) || 1))}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {recurringFrequency === 'daily' ? 'days' : 
                           recurringFrequency === 'weekly' ? 'weeks' : 
                           recurringFrequency === 'monthly' ? 'months' : 'years'}
                        </Typography>
                      ),
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                    inputProps={{ min: 1 }}
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Ends</InputLabel>
                    <Select
                      value={endType}
                      onChange={(e) => setEndType(e.target.value as 'date' | 'occurrences')}
                      label="Ends"
                    >
                      <MenuItem value="date">On Date</MenuItem>
                      <MenuItem value="occurrences">After Occurrences</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {endType === 'date' ? (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(date) => setEndDate(date)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                  ) : (
                    <TextField
                      label="Occurrences"
                      type="number"
                      value={occurrences || ''}
                      onChange={(e) => setOccurrences(parseInt(e.target.value) || null)}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary"
          variant="contained"
          disabled={loading || selectedDates.length === 0}
        >
          {loading ? 'Saving...' : 'Block Dates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateBlockerModal; 